import {
    contactInfoTypes,
    flowActionTypes,
    lcTypes,
    lgTypes,
    paramResponseTypes,
    paramTypes,
    requestStages,
} from './flowConstants.js';
import { BotError, botErrorCodes } from './BotError.js';
import { jqGetAjaxError } from '../tools/requestUtil.js';
import { isCoverageInRange } from '../tools/coverageChecker.js';
import { BookingAvailability, BookingLocationInfo } from './bookingModels.js';

/**
 * Bot requests/response/etc.
 * Note: adding methods here may require adding methods to the PreviewBotService too
 */
export default class BotService {
    constructor(
        templateEng,
        flow,
    ) {
        /** @prop nunjucks environment */
        this.templateEng = templateEng;
        /** @prop BotFlow */
        this.flow = flow;
    }

    /**
     * Prefix of path for some requests that vary between widget and preview.
     * Overriden in PreviewBotService
     * @param {String} channel Chat channel
     * @returns {String}
     */
    _getPathPrefix(channel = '') {
        return `/chat/session/${channel}`;
    }

    /**
     * Get a single faq.
     * @param {Number} faqId f
     * @param {String} faqLinkText faq 'view full answer' link text
     * @returns {Promise} Resolves to message string.
     */
    getFAQ(faqId, faqLinkText = '') {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `/chat/faq/${faqId}`,
                data: {
                    instid: this.flow.iid,
                },
            })
                .done((faq) => {
                    const text = this.templateEng.render('item_faq.njk', { faq, faqLinkText });
                    resolve(text);
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Search for FAQs.
     * @param {Number} groupId g
     * @param {String} query Search string from patron
     * @returns {Promise} Resolves to array of faqs and full search url.
     */
    #searchFAQs(groupId, query) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: '/chat/faq/search',
                data: {
                    iid: this.flow.iid,
                    group_ids: `[${groupId}]`,
                    q: query,
                },
            })
                .done((data) => {
                    if (!data.results || data.results.length === 0) {
                        reject(new BotError({
                            code: botErrorCodes.noResults,
                            message: 'No results found',
                        }));
                        return;
                    }
                    resolve({ faqs: data.results, searchUrl: data.fullSearchUrl });
                })
                .fail(() => {
                    reject(new BotError({ message: this.flow.error_generic }));
                });
        });
    }

    /**
     * Get a single guide.
     * @param {Number} guideId g
     * @param {Number} siteId LG Site id
     * @param {String} channel patron channel
     * @returns {Promise} Resolves to message text.
     */
    getGuide(guideId, siteId, channel) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libguides/guide/list`,
                data: {
                    guide_ids: [guideId],
                    site_id: siteId,
                    iid: this.flow.iid,
                },
            })
                .done((data) => {
                    if (Array.isArray(data) && data.length > 0) {
                        // for now just handling one.
                        const text = this.templateEng.render('item_guide.njk', { item: data.shift() });
                        resolve(text);
                        return;
                    }
                    reject(new Error('No guides found.'));
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Search for guides.
     * @param {Number} siteId LG site id
     * @param {String} channel patron channel
     * @param {String} term Search string from patron
     * @returns {Promise} Resolves to array of results.
     */
    #searchGuides(siteId, channel, term) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libguides/guide/search`,
                data: {
                    iid: this.flow.iid,
                    site_id: siteId,
                    term,
                    limit: 15,
                },
            })
                .done((data) => {
                    if (!Array.isArray(data) || data.length === 0) {
                        reject(new BotError({
                            code: botErrorCodes.noResults,
                            message: 'No results found',
                        }));
                        return;
                    }
                    resolve(data);
                })
                .fail(() => {
                    reject(new BotError({ message: this.flow.error_generic }));
                });
        });
    }

    /**
     * Get a single A-Z Database.
     * @param {Number} azId g
     * @param {Number} siteId LG Site id
     * @param {String} channel patron channel
     * @returns {Promise} Resolves to message text
     */
    getDatabase(azId, siteId, channel) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libguides/database/list`,
                data: {
                    asset_ids: [azId],
                    site_id: siteId,
                    iid: this.flow.iid,
                },
            })
                .done((data) => {
                    if (Array.isArray(data) && data.length > 0) {
                        // for now just handling one.
                        const text = this.templateEng.render('item_guide.njk', { item: data.shift() });
                        resolve(text);
                        return;
                    }
                    reject(new Error('No databases found.'));
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Search for databases.
     * @param {Number} siteId LG site id
     * @param {String} channel patron channel
     * @param {String} term Search string from patron
     * @returns {Promise} Resolves to array of results.
     */
    #searchDatabases(siteId, channel, term) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libguides/database/search`,
                data: {
                    iid: this.flow.iid,
                    site_id: siteId,
                    term,
                    limit: 5,
                },
            })
                .done((data) => {
                    if (!Array.isArray(data) || data.length === 0) {
                        reject(new BotError({
                            code: botErrorCodes.noResults,
                            message: 'No results found',
                        }));
                        return;
                    }
                    resolve(data);
                })
                .fail(() => {
                    reject(new BotError({ message: this.flow.error_generic }));
                });
        });
    }

    /**
     * Get a single LC location.
     * @param {LibCalAction} action LC action
     * @param {String} channel patron channel
     * @returns {Promise} Resolves to message text.
     */
    getLocation(action, channel) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/locations`,
                data: {
                    type: action.subType,
                    location_ids: [action.subTypeId],
                    site_id: action.siteId,
                    iid: this.flow.iid,
                },
            })
                .done((data) => {
                    if (Array.isArray(data) && data.length > 0) {
                        // for now just handling one.
                        const text = this.templateEng.render('item_guide.njk', { item: data.shift() });
                        resolve(text);
                        return;
                    }
                    reject(new Error('No locations found.'));
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Search for LC events.
     * @param {LibCalAction} action LC
     * @param {String} channel patron channel
     * @param {String} term Search string from patron
     * @returns {Promise} Resolves to array of results.
     */
    #searchEvents(action, channel, term) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/events/search`,
                data: {
                    iid: this.flow.iid,
                    site_id: action.siteId,
                    term,
                    calendar_id: action.subTypeId,
                    search_filter: JSON.stringify(action.searchFilter),
                },
            })
                .done((data) => {
                    if (!Array.isArray(data) || data.length === 0) {
                        reject(new BotError({
                            code: botErrorCodes.noResults,
                            message: 'No results found',
                        }));
                        return;
                    }
                    resolve(data);
                })
                .fail(() => {
                    reject(new BotError({ message: this.flow.error_generic }));
                });
        });
    }

    /**
     * Get LC hours info
     * @param {LibCalAction} action LC action
     * @param {String} channel patron channel
     * @returns {Promise}
     */
    getLCHours(action, channel) {
        let path = 'day';
        if (action.subType === lcTypes.HOURS_WEEK) {
            path = 'range';
        }
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/hours/library/${path}`,
                data: {
                    type: action.subType,
                    location_id: action.subTypeId,
                    site_id: action.siteId,
                    iid: this.flow.iid,
                },
            })
                .done(({ days = [], timezone = 'UTC' }) => {
                    if (Array.isArray(days) && days.length > 0) {
                        const text = [];
                        const locale = navigator.language || 'en-US';
                        const dateFormatter = new Intl.DateTimeFormat(
                            locale, { dateStyle: 'short', timeZone: timezone },
                        );
                        const timeFormatter = new Intl.DateTimeFormat(
                            locale, { timeStyle: 'short', timeZone: timezone },
                        );
                        days.forEach(({ date = '', hours = [], label = '', note = '' }) => {
                            let dayInfo = `${dateFormatter.format(new Date(date))}: `;
                            if (hours.length > 0) {
                                dayInfo += `${hours.map(({ to, from }) => {
                                    return `${timeFormatter.format(new Date(from))} - ${timeFormatter.format(new Date(to))}`;
                                }).join('; ')}`;
                            } else {
                                // should cover closed, 24hr, etc.
                                dayInfo += label || note;
                            }
                            text.push(dayInfo);
                        });
                        resolve(text.join('<br/>'));
                        return;
                    }
                    reject(new Error('No hours found.'));
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Get booking location info
     * @param {LibCalAction} action LC
     * @param {String} channel Current chat
     * @param {String} language Widget language
     * @returns {Promise}
     */
    getBookingLocationInfo(action, channel, language) {
        if (!action.apptParams) {
            throw new Error();
        }
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/booking/location`,
                data: {
                    type: action.subType,
                    location_id: action.subTypeId,
                    site_id: action.siteId,
                    iid: this.flow.iid,
                    group_id: action.apptParams?.groupId,
                    category_id: action.apptParams?.categoryId,
                    language,
                },
            })
                .done((data) => {
                    if (!data || typeof data !== 'object') {
                        reject(new Error('No location info found.'));
                        return;
                    }
                    const location = new BookingLocationInfo(data);
                    action.setLocationInfo(location);
                    resolve(true);
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Get booking date availability
     * @param {LibCalAction} action LC
     * @param {String} channel Current chat
     * @param {String} language Widget language
     * @returns {Promise<String>}
     */
    getBookingAvailability(action, channel, language) {
        const info = action.getBookingInfo();
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/booking/availability`,
                data: {
                    location_id: info.locationId,
                    site_id: info.siteId,
                    iid: this.flow.iid,
                    group_id: info.groupId,
                    category_id: info.categoryId,
                    user_id: info.userId,
                    date: info.date,
                    language,
                },
            })
                .done(({ availability = null, message = '' }) => {
                    if (!availability || typeof availability !== 'object') {
                        reject(new Error('No availability found.'));
                        return;
                    }
                    const avail = new BookingAvailability(availability);

                    // if there are no dates or times found
                    if (info.date !== '' && avail.times.length === 0) {
                        // no times found... reset to date stage
                        action.stage = action.getPreviousBookingStage();
                        resolve(message);
                        return;
                    } else if (info.date === '' && avail.dates.length === 0) {
                        // Go to stage before date... (which can vary a lot)
                        action.stage = action.getPreviousBookingStage();
                        resolve(message);
                        return;
                    }

                    action.setBookingAvailability(avail);
                    resolve('');
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Get LC booking form for a specific time slot
     * @param {LibCalAction} action LC
     * @param {String} channel Chat channel
     * @param {String} language Widget language
     * @returns {Promise<Object>}
     */
    getBookingForm(action, channel, language) {
        const info = action.getBookingInfo();
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/booking/form`,
                data: {
                    location_id: info.locationId,
                    site_id: info.siteId,
                    iid: this.flow.iid,
                    group_id: info.groupId,
                    category_id: info.categoryId,
                    user_id: info.userId,
                    start_time: info.getStartTime(),
                    crc: info.getCrc(),
                    language,
                },
            })
                .done(({ html = '' }) => {
                    if (html === '') {
                        reject(new Error('No form found.'));
                        return;
                    }
                    resolve({
                        html,
                    });
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Get LibWizard item
     * @param {LibWizardAction} action Current action
     * @param {String} channel patron channel
     * @returns {Promise} Resolves to message text.
     */
    getLibWizardItem(action, channel) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libwizard/forms`,
                data: {
                    type: action.subType,
                    item_id: action.subTypeId,
                    site_id: action.siteId,
                    iid: this.flow.iid,
                },
            })
                .done((data) => {
                    if (data.url && data.url !== '') {
                        // for now just handling one.
                        const text = this.templateEng.render('item_guide.njk', { item: data });
                        resolve(text);
                        return;
                    }
                    reject(new Error('No form found.'));
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Bot search actions.
     * @param {FlowAction} action a
     * @param {String} channel Patron channel
     * @param {String} query Search text
     * @returns {String} Message text
     * @throws {BotError} no results or error
     */
    async searchAction(action, channel, query) {
        if (action.type === flowActionTypes.ACTION_FAQ) {
            // faqs
            const { faqs, searchUrl } = await this.#searchFAQs(action.subTypeId, query);
            return this.templateEng.render('list_faqs.njk', {
                faqs,
                searchUrl,
            });
        }
        if (action.type === flowActionTypes.ACTION_LIBGUIDES) {
            let items = [];
            if (action.subType === lgTypes.GUIDE_SEARCH) {
                items = await this.#searchGuides(action.siteId, channel, query);
            } else if (action.subType === lgTypes.AZ_SEARCH) {
                items = await this.#searchDatabases(action.siteId, channel, query);
            }
            return this.templateEng.render('list_guides.njk', {
                items,
            });
        }
        if (action.type === flowActionTypes.ACTION_LIBCAL) {
            const items = await this.#searchEvents(
                action,
                channel,
                query,
            );
            return this.templateEng.render('list_guides.njk', {
                items,
            });
        }
        throw new BotError({ message: this.flow.error_generic });
    }

    /**
     * Submit a ticket.
     * @param {FlowAction} action Ticket action
     * @param {String} channel Session channel
     * @param {String} question Text from patron
     * @returns {Promise} Resolves to ticket id
     */
    submitTicket(action, channel, question) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `/chat/bot/session/${channel}/ticket`,
                // these are set to match what the qform sends
                data: {
                    iid: this.flow.iid,
                    question,
                    queue_id: action.subType,
                },
            })
                .done(({ ticket_id = 0 }) => {
                    if (ticket_id > 0) {
                        resolve({ ticket_id });
                        return;
                    }
                    reject(this.flow.error_generic);
                })
                .fail(() => {
                    reject(this.flow.error_generic);
                });
        });
    }

    /**
     * Save a question answer.
     * @param {FlowAction} action a
     * @param {String} channel Patron session
     * @param {Number} id Response id
     * @param {String} text Reponse text
     * @returns {Promise}
     */
    saveQuestionAnswer(action, channel, { id = 0, text = '' }) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `/chat/bot/session/${channel}/question/${action.subType}`,
                data: {
                    iid: this.flow.iid,
                    response_id: id,
                    text,
                },
            })
                .done(() => {
                    resolve();
                })
                .fail(() => {
                    reject(new BotError({
                        actionId: action.id,
                        message: this.flow.error_generic,
                    }));
                });
        });
    }

    /**
     * Save a contact request answer.
     * @param {FlowAction} action a
     * @param {String} channel Patron session
     * @param {String} text Reponse text
     * @returns {Promise}
     */
    saveContactInfo(action, channel, text) {
        let type = 0;
        if (action.type === flowActionTypes.ACTION_REQUEST_INFO) {
            switch (action.stage) {
                case requestStages.NAME:
                    type = contactInfoTypes.INFO_NAME;
                    break;
                case requestStages.EMAIL:
                    type = contactInfoTypes.INFO_EMAIL;
                    break;
                case requestStages.ID:
                    type = contactInfoTypes.INFO_ID;
                    break;
            }
        } else if (action.type === flowActionTypes.ACTION_TICKET) {
            type = contactInfoTypes.INFO_EMAIL;
        } else {
            throw new Error('Invalid action type');
        }
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `/chat/bot/session/${channel}/contact`,
                data: {
                    iid: this.flow.iid,
                    type,
                    text,
                },
            })
                .done(() => {
                    resolve();
                })
                .fail((jqxhr) => {
                    const message = jqGetAjaxError(jqxhr) || this.flow.error_generic;
                    let code = botErrorCodes.generic;
                    let end_chat = false;
                    // HttpConstants::STATUS_FORBIDDEN for banned/blocked
                    if (jqxhr && jqxhr.status === 403) {
                        code = botErrorCodes.botBlockedError;
                        end_chat = true;
                    }
                    reject(new BotError({
                        code,
                        message,
                        actionId: action.id,
                        end_chat,
                    }));
                });
        });
    }

    /**
     * Tell server session ended.
     * @param {String} channel session id
     * @returns {Promise}
     */
    saveEndSession(channel) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `/chat/bot/session/${channel}/end`,
                data: {
                    iid: this.flow.iid,
                },
            })
                .done(() => {
                    resolve();
                })
                .fail(() => {
                    reject(new Error('Failed'));
                });
        });
    }

    /**
     * Save actions not covered elsewhere.
     * No return, no await, fire and forget.
     * @param {String} channel Patron channel.
     * @param {FlowAction} action Action to record
     * @param {String} response Response text
     * @param {Number} response_id Response id.
     */
    saveSessionAction(channel, action, { response = '', response_id = 0 }) {
        window.$.ajax({
            method: 'POST',
            url: `/chat/bot/session/${channel}/action`,
            data: {
                iid: this.flow.iid,
                type: action.type,
                sub_type: action.subType,
                object_id: response_id,
                response,
                time: Date.now(),
            },
        })
            .done(() => {
                // no op
            })
            .fail((jqxhr) => {
                const message = jqGetAjaxError(jqxhr);

                console.error(message);
            });
    }

    /**
     * Get question form html/data.
     * @param {String} channel Patron channel
     * @param {FlowAction} action Ticket action
     * @returns {Promise}
     */
    getQuestionForm(channel, action) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/qform`,
                data: {
                    iid: this.flow.iid,
                    queue_id: action.subType,
                },
            })
                // Returns form html
                .done(({
                    html = '',
                    errormsg = [],
                    translations = [],
                    isAuthed = false,
                    autoReplySettings = {},
                    url = '',
                    rules = [],
                }) => {
                    resolve({ html, errormsg, translations, isAuthed, autoReplySettings, url, rules });
                })
                .fail(() => {
                    reject(new BotError({ message: this.flow.error_generic }));
                });
        });
    }

    /**
     * Load a new flow for transfer.
     * @param {String} channel Patron session id
     * @param {String} flow_uuid New flow to load
     * @returns {Promise} Resolves to flow json object
     */
    getFlowTransfer(channel, flow_uuid) {
        // get new flow AND record the transfer in session actions
        // return flow I guess
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `${this._getPathPrefix(channel)}/bottransfer`,
                data: {
                    iid: this.flow.iid,
                    flow_uuid,
                },
            })
                .done(({ flow = null }) => {
                    resolve(flow);
                })
                .fail((jqxhr) => {
                    const message = jqGetAjaxError(jqxhr) || this.flow.error_generic;
                    reject(new BotError({
                        code: botErrorCodes.generic,
                        message,
                    }));
                });
        });
    }

    /**
     * Tell server user dismissed the chatbot.
     * @param {int} widgetId widget id
     * @returns {Promise}
     */
    saveDismissal(widgetId) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `/chat/bot/widget/${widgetId}/dismiss`,
                data: {
                    iid: this.flow.iid,
                },
            })
                .done(() => {
                    resolve();
                })
                .fail(() => {
                    reject(new Error('Failed'));
                });
        });
    }

    /**
     * Check custom open hours.
     * Overriden in PreviewBotService to act differently
     * @param {ParamAction} action Custom open hours action
     * @returns {Promise} Resolves to constant for open/closed.
     */
    checkOpenHours(action) {
        if (action.type !== flowActionTypes.ACTION_PARAMS
            || action.subType !== paramTypes.PARAM_OPEN_HOURS) {
            throw new Error('Invalid action.');
        }
        // this returns a promise cause the preview version has to do an ajax request
        return new Promise((resolve) => {
            const times = action.customHoursObj;
            const now = new Date();
            const day = now.getUTCDay();
            const hour = now.getUTCHours();
            const minute = now.getUTCMinutes();
            const ranges = times[day] || [];
            if (isCoverageInRange(hour, minute, ranges)) {
                // open
                resolve(paramResponseTypes.PARAM_HOURS_OPEN);
                return;
            }
            resolve(paramResponseTypes.PARAM_HOURS_CLOSED);
        });
    }

    /**
     * Check LC open hours.
     * Overriden in PreviewBotService to act differently
     * @param {ParamAction} action Custom open hours action
     * @param {String} channel Current chat
     * @returns {Promise} Resolves to constant for open/closed.
     */
    checkLCHours(action, channel) {
        if (action.type !== flowActionTypes.ACTION_PARAMS
            || action.subType !== paramTypes.PARAM_LC_HOURS) {
            throw new Error('Invalid action.');
        }
        return new Promise((resolve) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/libcal/hours/open`,
                data: {
                    location_id: action.subTypeId,
                    site_id: action.siteId,
                    iid: this.flow.iid,
                },
            })
                .done(({ open = false }) => {
                    resolve(open ? paramResponseTypes.PARAM_HOURS_OPEN : paramResponseTypes.PARAM_HOURS_CLOSED);
                })
                .fail(() => {
                    // just fallback to closed
                    resolve(paramResponseTypes.PARAM_HOURS_CLOSED);
                });
        });
    }

    /**
     * URL path for patron records action
     * @param {Number} id
     * @returns {String}
     */
    #getRecordPath(id) {
        switch (id) {
            case 1:
                return 'checkouts';
            case 2:
                return 'holds';
            case 3:
                return 'fines';
            default:
                return '';
        }
    }

    /**
     * @param {PatronRecordsAction} action
     * @param {Number} id Response id
     * @param {String} patron_id
     * @param {String} patron_pin
     * @param {String} channel
     * @returns {Promise} Resolves to html.
     */
    getPatronRecordData(action, id, patron_id, patron_pin, channel) {
        const path = this.#getRecordPath(id);
        if (!path) {
            throw new Error('Invalid action');
        }
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'GET',
                url: `${this._getPathPrefix(channel)}/patron/${path}`,
                data: {
                    patron_id,
                    patron_pin,
                    iid: this.flow.iid,
                },
            })
                .done(({ html = '' }) => {
                    resolve(html);
                })
                .fail((jqxhr) => {
                    const message = jqGetAjaxError(jqxhr) || this.flow.error_generic;
                    reject(new BotError({
                        code: botErrorCodes.generic,
                        message,
                    }));
                });
        });
    }

    /**
     * @param {String} itemId Id of item to renew
     * @param {String} patron_id
     * @param {String} patron_pin
     * @param {String} channel
     * @returns {Promise} Resolves to html.
     */
    renewPatronItem(itemId, patron_id, patron_pin, channel) {
        return new Promise((resolve, reject) => {
            window.$.ajax({
                method: 'POST',
                url: `${this._getPathPrefix(channel)}/patron/itemrenew`,
                data: {
                    id: itemId,
                    patron_id,
                    patron_pin,
                    iid: this.flow.iid,
                },
            })
                .done(({ message = 'Error' }) => {
                    resolve(message);
                })
                .fail((jqxhr) => {
                    const message = jqGetAjaxError(jqxhr) || this.flow.error_generic;
                    reject(new BotError({
                        code: botErrorCodes.generic,
                        message,
                    }));
                });
        });
    }
}
